import React from 'react';
import { Col, Row, Card, CardBody, CardHeader, Badge, Collapse, Input } from 'reactstrap';
import * as utils from '../../Services/utils';
import { useTranslation } from 'react-i18next';
import Slider from 'rc-slider';
import '../../Components/css/rc-slider.css';
import { sliderMarks } from '../../Helpers/applicantHelper';
import { Rating } from 'react-simple-star-rating';
import { InfoTag } from './infoTag';

const CompetenceScoreExplanation = ({ application, i18nLanguage, isOpen, toggleAccordion }) => {
  const { t } = useTranslation();

  // Memoized helper to render the score badge
  const renderCompetenceType = React.useMemo(
    () => (item) => {
      const { competenceType, competenceWeight, estimatedExperience } = item;

      const experienceValue = competenceType === 'Boolean' ? estimatedExperience * 10 : estimatedExperience;
      const scoreValue = competenceWeight * experienceValue;

      return (
        <h4>
          x{competenceWeight} <em className="fa mr-1 fas fa-lock" />= {scoreValue}
        </h4>
      );
    },
    []
  );

  const ScoreRow = ({ label, score, infoTagId, i18nLanguage }) => (
    <Row className="align-items-center my-1">
      <Col>
        <h4 className="m-0 p-0">
          <strong>{label}: </strong>
          {score}
          {infoTagId && (
            <small className="text-muted ml-1">
              <InfoTag id={infoTagId} lng={i18nLanguage} />
            </small>
          )}
        </h4>
      </Col>
    </Row>
  );

  return (
    <>
      <Row>
        <Col className="mt-4" style={{ cursor: 'pointer' }} onClick={toggleAccordion}>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="mb-2">
              <small>
                <em className={isOpen ? 'fa fa-minus text-primary mr-2' : 'fa fa-plus text-primary mr-2'}></em>
              </small>
              {t('competences.SCORE_FROM_COMPETENCES')}
            </h3>
            {application?.score?.applicantCompetenceAnalisys && (
              <Rating
                initialValue={(application.score.combined / application.job.maxPossibleScore) * 5}
                size={25}
                readonly
                allowFraction
              />
            )}
          </div>
        </Col>
      </Row>
      <Collapse isOpen={isOpen}>
        {application?.score?.applicantCompetenceAnalisys ? (
          <>
            <Row>
              <Col>
                <Card className="w-auto d-inline-block border shadow-sm bg-light mb-3">
                  <CardBody>
                    <div className="mb-2">
                      <ScoreRow
                        label={t('competences.SCORE_POSITIVE')}
                        score={application.score.positive}
                        infoTagId="positiveScorePart"
                        i18nLanguage={i18nLanguage}
                      />
                      <ScoreRow
                        label={t('competences.SCORE_NEGATIVE')}
                        score={application.score.negative}
                        infoTagId="negativeScorePart"
                        i18nLanguage={i18nLanguage}
                      />
                    </div>
                    <div className="border-top pt-2">
                      <h3 className="mb-0">
                        <strong>Sum:</strong>
                        <strong className="ml-1 mb-0 text-primary">
                          {application.score.combined}/{application.job.maxPossibleScore}
                        </strong>
                      </h3>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              {application.score.applicantCompetenceAnalisys.map((item) => {
                const {
                  competenceName,
                  competenceType,
                  competenceId,
                  explanation,
                  competenceWeight,
                  estimatedExperience,
                } = item;
                return (
                  <Col key={competenceId} md="6" className="mb-2">
                    <Card className="border shadow-sm bg-light">
                      <CardHeader className="bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <h4 className="mb-0">{competenceName}</h4>
                          <Badge color="light" className={competenceType === 'Inverse' ? 'text-danger' : 'text-strong'}>
                            {utils.translateCompetenceType(competenceType)}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardBody>
                        {competenceType === 'Boolean' ? (
                          <Row className="mb-2  justify-content-between">
                            <Col lg="9">
                              <label className="c-radio">
                                <Input type="radio" value={1} checked={estimatedExperience} />
                                <span className="fa fa-circle"></span>
                                {t('common.YES')}
                              </label>
                              <label className="c-radio">
                                <Input type="radio" value={0} checked={!estimatedExperience} />
                                <span className="fa fa-circle"></span>
                                {t('common.NO')}
                              </label>
                            </Col>
                            <Col>{renderCompetenceType({ competenceType, competenceWeight, estimatedExperience })}</Col>
                          </Row>
                        ) : (
                          <Row className="mb-2  justify-content-between">
                            <Col lg="9" className="text-center score-explanation-slider">
                              <Slider
                                max={10}
                                min={0}
                                marks={sliderMarks}
                                value={estimatedExperience}
                                dots
                                step={1}
                                defaultValue={0}
                              />
                            </Col>
                            <Col>{renderCompetenceType({ competenceType, competenceWeight, estimatedExperience })}</Col>
                          </Row>
                        )}
                        {explanation && (
                          <Row className="mt-3">
                            <Col>
                              <strong>{t('applications.APPLICANT_EXPLANATION')}:</strong>
                              <div className="mt-1" dangerouslySetInnerHTML={{ __html: explanation }} />
                            </Col>
                          </Row>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        ) : (
          <Col className="text-warning">
            <strong>{t('competences.ADD_COMPETENCES_TO_ENABLE_SCORE')}</strong>
          </Col>
        )}
      </Collapse>
    </>
  );
};

export default CompetenceScoreExplanation;
